<template>
  <div class="rounded-2xl border border-[var(--ui-border)] bg-[var(--ui-bg)] p-3">
    <div class="mb-3 flex items-center justify-between gap-2">
      <UButton
        size="xs"
        variant="ghost"
        color="neutral"
        icon="i-lucide-chevron-right"
        aria-label="ماه قبل"
        :class="pickerOpen ? 'invisible' : ''"
        @click="shiftMonth(-1)"
      />
      <button
        type="button"
        class="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-semibold transition-colors hover:bg-[var(--ui-bg-elevated)]"
        :class="pickerOpen ? 'bg-[var(--ui-bg-elevated)]' : ''"
        @click="pickerOpen = !pickerOpen"
      >
        {{ JALALI_MONTHS[view.jm - 1] }} {{ toFaDigits(view.jy) }}
        <UIcon
          name="i-lucide-chevron-down"
          class="size-3.5 transition-transform duration-200"
          :class="pickerOpen ? 'rotate-180' : ''"
        />
      </button>
      <UButton
        size="xs"
        variant="ghost"
        color="neutral"
        icon="i-lucide-chevron-left"
        aria-label="ماه بعد"
        :class="pickerOpen ? 'invisible' : ''"
        @click="shiftMonth(1)"
      />
    </div>

    <div class="relative">
      <div
        v-if="pickerOpen"
        class="absolute inset-0 z-10 flex flex-col gap-2 bg-[var(--ui-bg)]"
      >
        <div class="flex items-center justify-between gap-2 rounded-lg bg-[var(--ui-bg-muted)] p-1">
          <UButton
            size="xs"
            variant="ghost"
            color="neutral"
            icon="i-lucide-chevron-right"
            aria-label="سال قبل"
            @click="shiftYear(-1)"
          />
          <span class="text-sm font-semibold tabular-nums">{{ toFaDigits(view.jy) }}</span>
          <UButton
            size="xs"
            variant="ghost"
            color="neutral"
            icon="i-lucide-chevron-left"
            aria-label="سال بعد"
            @click="shiftYear(1)"
          />
        </div>
        <div class="grid flex-1 grid-cols-3 grid-rows-4 gap-1">
          <button
            v-for="(month, index) in JALALI_MONTHS"
            :key="month"
            type="button"
            class="rounded-lg text-xs transition-colors duration-150"
            :class="view.jm === index + 1
              ? 'bg-[var(--ui-primary)] font-semibold text-white'
              : 'text-[var(--ui-text-toned)] hover:bg-[var(--ui-bg-elevated)]'"
            @click="selectMonth(index + 1)"
          >
            {{ month }}
          </button>
        </div>
      </div>

      <div
        class="grid grid-cols-7 gap-1 text-center"
        :class="pickerOpen ? 'invisible' : ''"
      >
      <span
        v-for="label in JALALI_WEEKDAY_SHORT"
        :key="label"
        class="pb-1 text-[11px] text-[var(--ui-text-muted)]"
      >
        {{ label }}
      </span>

      <span
        v-for="blank in leadingBlanks"
        :key="`blank-${blank}`"
      />

      <button
        v-for="cell in cells"
        :key="cell.iso"
        type="button"
        :disabled="cell.disabled"
        :title="cell.holiday ? cell.holiday.title : undefined"
        class="relative flex aspect-square items-center justify-center rounded-lg text-sm tabular-nums transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-30"
        :class="cellClass(cell)"
        @click="emit('update:modelValue', cell.iso)"
      >
        {{ toFaDigits(cell.day) }}
        <span
          v-if="(cell.marked || cell.available) && cell.iso !== modelValue"
          class="absolute bottom-1 size-1 rounded-full"
          :class="cell.marked ? 'bg-[var(--ui-warning)]' : 'bg-[var(--ui-success)]'"
        />
        </button>
      </div>
    </div>

    <div
      v-if="showLegend"
      class="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-[var(--ui-border)] pt-2.5 text-[11px] text-[var(--ui-text-muted)]"
    >
      <span class="inline-flex items-center gap-1">
        <span class="size-2 rounded-full bg-[var(--ui-error)]" />
        تعطیل رسمی
      </span>
      <span
        v-if="availableDates.length"
        class="inline-flex items-center gap-1"
      >
        <span class="size-2 rounded-full bg-[var(--ui-success)]" />
        نوبت آزاد
      </span>
      <span
        v-if="markedDates.length"
        class="inline-flex items-center gap-1"
      >
        <span class="size-2 rounded-full bg-[var(--ui-warning)]" />
        استثنای ثبت‌شده
      </span>
      <span class="inline-flex items-center gap-1">
        <span class="size-2 rounded-full ring-1 ring-[var(--ui-primary)]" />
        امروز
      </span>
    </div>

  </div>
</template>

<script setup lang="ts">
import type { HolidayInfo } from '~/utils/iranHolidays'
import { toFaDigits } from '~/utils/faDigits'
import { iranHoliday } from '~/utils/iranHolidays'
import {
  addJalaliMonths,
  isoToJalali,
  JALALI_MONTHS,
  JALALI_WEEKDAY_SHORT,
  jalaliMonthLength,
  jalaliToIso,
  jalaliWeekday,
} from '~/utils/jalali'
import { tehranToday } from '~/utils/tehran'

interface DayCell {
  iso: string
  day: number
  disabled: boolean
  isToday: boolean
  holiday: HolidayInfo | null
  marked: boolean
  available: boolean
}

const props = withDefaults(defineProps<{
  modelValue?: string | null
  /** Dates that already have something attached to them (e.g. exceptions). */
  markedDates?: string[]
  /** Dates with free capacity; shown with a green dot. */
  availableDates?: string[]
  minDate?: string
  maxDate?: string
  showLegend?: boolean
  /** Initial month when value is empty (YYYY-MM-DD). */
  fallbackDate?: string
}>(), {
  modelValue: '',
  markedDates: () => [],
  availableDates: () => [],
  minDate: '',
  maxDate: '',
  showLegend: true,
  fallbackDate: '',
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  /** Visible month changed — emits the Gregorian range so callers can prefetch. */
  'month-change': [range: { from: string; to: string }]
}>()

const today = tehranToday()
const seedIso = (props.modelValue && /^\d{4}-\d{2}-\d{2}$/.test(props.modelValue))
  ? props.modelValue
  : (props.fallbackDate && /^\d{4}-\d{2}-\d{2}$/.test(props.fallbackDate) ? props.fallbackDate : today)
const initial = isoToJalali(seedIso)
const view = ref({ jy: initial.jy, jm: initial.jm })
const pickerOpen = ref(false)

const leadingBlanks = computed(() => jalaliWeekday(view.value.jy, view.value.jm, 1))

const cells = computed<DayCell[]>(() => {
  const length = jalaliMonthLength(view.value.jy, view.value.jm)
  return Array.from({ length }, (_, index) => {
    const day = index + 1
    const iso = jalaliToIso(view.value.jy, view.value.jm, day)
    return {
      iso,
      day,
      disabled: (Boolean(props.minDate) && iso < props.minDate)
        || (Boolean(props.maxDate) && iso > props.maxDate),
      isToday: iso === today,
      holiday: iranHoliday(iso),
      marked: props.markedDates.includes(iso),
      available: props.availableDates.includes(iso),
    }
  })
})

watch(cells, (list) => {
  const from = list[0]?.iso
  const to = list[list.length - 1]?.iso
  if (from && to) emit('month-change', { from, to })
}, { immediate: true })

function cellClass(cell: DayCell) {
  if (props.modelValue && cell.iso === props.modelValue) return 'bg-[var(--ui-primary)] font-bold text-white'
  const rings = cell.isToday ? ' ring-1 ring-[var(--ui-primary)]' : ''
  if (cell.holiday) return `font-medium text-[var(--ui-error)] hover:bg-[var(--ui-error)]/10${rings}`
  return `text-[var(--ui-text-toned)] hover:bg-[var(--ui-bg-elevated)]${rings}`
}

function shiftMonth(delta: number) {
  view.value = addJalaliMonths(view.value.jy, view.value.jm, delta)
}

function shiftYear(delta: number) {
  view.value = { ...view.value, jy: view.value.jy + delta }
}

function selectMonth(jm: number) {
  view.value = { ...view.value, jm }
  pickerOpen.value = false
}

watch(() => props.modelValue, (value) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return
  const parts = isoToJalali(value)
  if (parts.jy !== view.value.jy || parts.jm !== view.value.jm) {
    view.value = { jy: parts.jy, jm: parts.jm }
  }
})
</script>
